import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, BehaviorSubject, of } from 'rxjs';
import { catchError, filter, take, switchMap, finalize } from 'rxjs/operators';
import { AuthService } from '@auth0/auth0-angular';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/store/app.store';
import { updateToken, logout } from 'src/app/store/actions/auth.actions';
import { Router } from '@angular/router';
import { AuthTokenService } from '../services/auth-token.service';

@Injectable()
export class AuthTokenInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

  constructor(
    private auth: AuthService,
    private store: Store<AppState>,
    private router: Router,
    private authTokenService: AuthTokenService
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Skip token handling for Auth0 endpoints
    if (request.url.includes('auth0.com')) {
      return next.handle(request);
    }

    // Wait for both token and org_id to be ready before proceeding
    return this.authTokenService.waitForAuth().pipe(
      switchMap(isReady => {
        if (!isReady) {
          return throwError(() => new Error('Authentication not ready'));
        }
        return this.handleRequest(request, next);
      })
    );
  }

  private handleRequest(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return this.authTokenService.getTokenWithFallback().pipe(
      switchMap(token => {
        if (token) {
          request = this.addToken(request, token);
        }
        return next.handle(request).pipe(
          catchError(error => {
            if (error instanceof HttpErrorResponse && error.status === 401) {
              return this.handle401Error(request, next);
            }
            return throwError(() => error);
          })
        );
      })
    );
  }

  private addToken(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`
      }
    });
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      console.log('Starting token refresh due to 401 error');
      
      // Use Auth0's SDK to silently refresh the token
      return this.auth.getAccessTokenSilently().pipe(
        switchMap((newToken: string) => {
          console.log('Token refreshed successfully');
          this.isRefreshing = false;
          this.refreshTokenSubject.next(newToken);
          
          // Update token in store
          this.store.dispatch(updateToken({ token: newToken }));
          
          // Retry the original request with the new token
          return next.handle(this.addToken(request, newToken));
        }),
        catchError(error => {
          this.isRefreshing = false;
          
          console.error('Token refresh failed with error:', error);
          
          // Check for Auth0 specific refresh token error
          if (this.isRefreshTokenError(error)) {
            console.error('Invalid refresh token detected. Logging out user.');
            this.handleLogout();
            // Navigate to login page instead of retrying
            this.router.navigate(['/login']);
            return throwError(() => new Error('Session expired. Please login again.'));
          }
          
          return throwError(() => error);
        }),
        finalize(() => {
          this.isRefreshing = false;
        })
      );
    } else {
      console.log('Already refreshing token, waiting for completion');
      // Wait for the token to be refreshed
      return this.refreshTokenSubject.pipe(
        filter(token => token !== null),
        take(1),
        switchMap(token => {
          console.log('Using newly refreshed token for request');
          return next.handle(this.addToken(request, token!));
        })
      );
    }
  }

  private isRefreshTokenError(error: any): boolean {
    return (
      error?.error === 'invalid_grant' && 
      error?.error_description?.includes('Unknown or invalid refresh token') &&
      error?.status === 403
    );
  }

  private handleLogout(): void {
    // Clear auth data
    localStorage.removeItem('auth');
    localStorage.removeItem('org_id');
    localStorage.removeItem('token_expires_at');
    localStorage.removeItem('roles');
    
    // Clear site-related data
    localStorage.removeItem('sites');
    localStorage.removeItem('site');
    localStorage.removeItem('types');
    localStorage.removeItem('type');
    localStorage.removeItem('statistics');
    
    // Clear session storage
    sessionStorage.removeItem('auth_in_progress');
    sessionStorage.removeItem('handling_auth');
    
    // Dispatch logout action
    this.store.dispatch(logout());
  }
} 